import React, { useCallback, useMemo, RefObject } from 'react';
import { createPortal } from 'react-dom';
import {
  AutoScrollActivator,
  closestCenter,
  CollisionDetection,
  DndContext,
  DndContextProps,
  DragCancelEvent,
  DragEndEvent,
  DragStartEvent,
  useSensor,
  useSensors,
} from '@wix/patterns-dnd-kit-core';
import {
  hasSortableData,
  SortableContext,
  SortableContextProps,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@wix/patterns-dnd-kit-sortable';
import { useUniqueId } from '@wix/patterns-dnd-kit-utilities';
import type { TableProps } from '@wix/design-system';
import { KeyboardSensor, PointerSensor } from './sensors';
import { classes, st } from './DroppableTableContext.st.css.js';
import { TableTestWrapper } from './TestWrappers';
import { useDragAndDropContext } from './DragAndDropContext';
import { TableRowDragOverlay } from './TableRowDragOverlay';
import { TableRowHorizontalScrollDragOverlay } from './TableRowHorizontalScrollDragOverlay';
import { restrictKeyboardToVerticalAxis } from './restrictKeyboardToVerticalAxis';
import '../../styles.global.css';

type TableElementProps = TableProps<{
  id: string | number;
}> &
  React.HTMLAttributes<HTMLTableElement> & {
    ref?: RefObject<HTMLElement>;
  };

type TableElement = React.FunctionComponentElement<TableElementProps>;

export interface DroppableTableContextProps {
  children: TableElement;
  items: SortableContextProps['items'];
  onDragEnd?(event: DragEndEvent): unknown;
  onDragStart?(event: DragStartEvent): unknown;
  onDragCancel?(event: DragCancelEvent): unknown;
  isDragAndDropDisabled?: boolean;
  horizontalScroll?: boolean;
  className?: string;
  style?: React.CSSProperties;
  renderRow?: (args: {
    rowNum: number;
    rowData: any;
    isDragOverlay: boolean;
  }) => React.ReactElement;
  renderTableContainer?: (children?: React.ReactElement) => React.ReactElement;
}

export function DroppableTableContext(props: DroppableTableContextProps) {
  const {
    children,
    items,
    className,
    style,
    onDragEnd,
    onDragStart,
    onDragCancel,
    isDragAndDropDisabled,
    horizontalScroll,
    renderRow,
    renderTableContainer,
  } = props;
  const { type: Table, props: tableProps, ref } = children;

  const {
    cancelDrop,
    announcements,
    onDragOver,
    onDragMove,
    a11yContainer,
    keyboardCodes,
    consumers,
    restoreFocus,
    autoScrollOptions,
    closestCenterOnViewPort,
  } = useDragAndDropContext();
  const [activeIndex, setActiveIndex] = React.useState<number | null>(null);
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 1,
    },
  });
  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
    keyboardCodes,
  });
  const uniqueSortableId = useUniqueId('Sortable');

  const onDragStartHandler = useCallback(
    (event: DragStartEvent) => {
      if (hasSortableData(event.active)) {
        setActiveIndex(event.active.data.current.sortable.index);
      }
      onDragStart?.(event);
    },
    [onDragStart],
  );

  const onDragEndHandler = useCallback(
    (event: DragEndEvent) => {
      setActiveIndex(null);
      onDragEnd?.(event);
    },
    [onDragEnd],
  );

  const onDragCancelHandler = useCallback(
    (event: DragCancelEvent) => {
      setActiveIndex(null);
      onDragCancel?.(event);
    },
    [onDragCancel],
  );

  const sensors = useSensors(
    ...(process.env.NODE_ENV === 'test' ? [] : [pointerSensor, keyboardSensor]),
  );

  const calcClosestCenterOnViewPort: CollisionDetection = (params) => {
    if (ref) {
      const filteredDroppableContainers = params.droppableContainers.filter(
        (item) => {
          if (!item.node.current || !ref.current) {
            return false;
          }

          const itemRect = item.node.current.getBoundingClientRect();

          return itemRect.top >= 0 && itemRect.bottom <= window.innerHeight;
        },
      );

      return closestCenter({
        ...params,
        droppableContainers: filteredDroppableContainers,
      });
    }
    return closestCenter(params);
  };

  const renderTableOverlay = () => {
    if (activeIndex === null) {
      return null;
    }

    let child = (
      <table className={className} style={style}>
        <tbody>
          {renderRow?.({
            rowNum: activeIndex,
            rowData: items[activeIndex],
            isDragOverlay: true,
          })}
        </tbody>
      </table>
    );

    if (renderTableContainer) {
      child = renderTableContainer(child);
    }

    return child;
  };

  const autoScroll = useMemo<DndContextProps['autoScroll']>(
    () => ({
      activator: AutoScrollActivator.DraggableRect,
      acceleration: 4,
      canScroll: (element) => {
        if (
          Array.from(element.classList).some((classListItem) =>
            classListItem.includes('tableBodyScrollContent'),
          )
        ) {
          return false;
        }

        return true;
      },
      freeze: true,
      threshold: {
        y: 0.1,
        x: 0,
      },
      ...autoScrollOptions,
    }),
    [autoScrollOptions],
  );

  const DragOverlayType = horizontalScroll
    ? TableRowHorizontalScrollDragOverlay
    : TableRowDragOverlay;

  const modifiers = useMemo(
    () => (horizontalScroll ? [restrictKeyboardToVerticalAxis] : []),
    [horizontalScroll],
  );

  let dndContext = (
    <DndContext
      sensors={sensors}
      accessibility={{
        container: a11yContainer as Element | undefined,
        announcements,
        screenReaderInstructions: {
          draggable: '',
        },
        restoreFocus,
      }}
      autoScroll={autoScroll}
      collisionDetection={
        closestCenterOnViewPort ? calcClosestCenterOnViewPort : closestCenter
      }
      onDragStart={onDragStartHandler}
      onDragCancel={onDragCancelHandler}
      onDragEnd={onDragEndHandler}
      onDragOver={onDragOver}
      onDragMove={onDragMove}
      cancelDrop={cancelDrop}
      modifiers={modifiers}
    >
      <SortableContext
        id={uniqueSortableId}
        items={items}
        strategy={verticalListSortingStrategy}
      >
        <Table
          {...tableProps}
          ref={ref}
          className={st(
            classes.root,
            {
              droppable: !isDragAndDropDisabled,
            },
            tableProps.className,
          )}
          {...announcements?.listAttributes?.()}
        />
        {consumers}
      </SortableContext>
      {createPortal(
        <DragOverlayType>{renderTableOverlay()}</DragOverlayType>,
        a11yContainer ?? document.body,
      )}
    </DndContext>
  );

  if (process.env.NODE_ENV === 'test') {
    dndContext = (
      <TableTestWrapper sortableId={uniqueSortableId}>
        {dndContext}
      </TableTestWrapper>
    );
  }

  return dndContext;
}
